import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

export interface GridColumnToggle { key: string; label: string; hidden: boolean }

export interface GridSelection {
  /** Whether the tick column is showing. */
  active: boolean;
  setActive: (on: boolean) => void;
  count: number;
  clear: () => void;
}

/**
 * What the grid on this page can do, published for the header to drive.
 *
 * Callbacks live behind a ref inside the provider, so the header re-renders when the NUMBERS
 * change - the search text, which columns are hidden, how many rows are ticked - and not every
 * time the grid re-renders and makes new closures. Publishing the object itself on every render
 * is how this kind of context turns into an infinite loop.
 */
export interface GridControls {
  search: string;
  setSearch: (value: string) => void;
  searchPlaceholder: string;
  columns: GridColumnToggle[];
  toggleColumn: (key: string) => void;
  /** Undefined when the role may not export, so the header simply does not offer it. */
  exportCsv?: () => void;
  /** Undefined when the grid was not given a way to select rows. */
  selection?: GridSelection;
  /**
   * Column filtering, switched from the header (owner, 2026-09-27: "add filter button to header
   * as icon and on click displays the filter icon on columns"). `count` is how many columns are
   * narrowing the grid right now, so the header can mark the icon while any are.
   */
  filtering: { active: boolean; setActive: (on: boolean) => void; count: number };
  /** True when a search or a column filter is narrowing the grid. */
  narrowed: boolean;
  clearAll: () => void;
}

interface Store {
  controls: GridControls | null;
  /**
   * `owner` identifies the grid. A grid clears the header only if the header is still showing
   * ITS controls: a drawer opened over a register has its own grid, and when that drawer closes
   * it must not blank a header the register underneath is still driving.
   */
  publish: (owner: symbol, controls: GridControls | null) => void;
}

const GridToolbarContext = createContext<Store>({ controls: null, publish: () => {} });

/**
 * The grid's controls, moved into the page header (owner, 2026-09-25: "shift the columns and
 * export icon to the header after search icon in the datagrid in pages? Drop the search on
 * datagrid as we have in header").
 *
 * ONE GRID AT A TIME. A page shows one register, and a drawer opened over it may show another -
 * the last one to publish wins, which is the one on top and the one the header should be driving.
 */
export function GridToolbarProvider({ children }: { children: ReactNode }) {
  const [controls, setControls] = useState<GridControls | null>(null);
  const owner = useRef<symbol | null>(null);

  const publish = useCallback((from: symbol, next: GridControls | null) => {
    if (next === null) {
      // Only the grid that owns the header may take the controls away.
      if (owner.current !== from) return;
      owner.current = null;
      setControls(null);
      return;
    }
    owner.current = from;
    setControls(next);
  }, []);

  const value = useMemo(() => ({ controls, publish }), [controls, publish]);
  return <GridToolbarContext.Provider value={value}>{children}</GridToolbarContext.Provider>;
}

export function useGridToolbar() {
  return useContext(GridToolbarContext);
}
